import React from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import styles from './MiniProductCard.module.scss';

export interface MiniProductCardProps {
  displayName: string;
  displayPrice: string;
  displaySalePrice?: string;
  imageURL: string;
  href: string;
  title?: string;
  withBorder?: boolean;
  priority?: boolean;
  withBoxShadow?: boolean;
}

const MiniProductCard = ({
  displayName,
  displayPrice,
  displaySalePrice,
  imageURL,
  href,
  title,
  withBorder = false,
  priority = false,
  withBoxShadow = true,
}: MiniProductCardProps) => {
  const { t } = useTranslation('common');

  const displaySale = !!displaySalePrice;

  const parsePrice = (value?: string) => {
    const cleaned = (value ?? '').replace(/[^\d.,-]/g, '');
    if (!cleaned) return 0;

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    const normalized =
      hasComma && !hasDot
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '');

    return parseFloat(normalized) || 0;
  };

  const parsedDisplayPrice = parsePrice(displayPrice);
  const parsedSalePrice = parsePrice(displaySalePrice);
  const discountPercent =
    displaySale && parsedDisplayPrice > 0
      ? Math.round((1 - parsedSalePrice / parsedDisplayPrice) * 100)
      : 0;

  const ariaLabel = `${displayName}, ${displaySalePrice || displayPrice}`;

  return (
    <article
      className={styles.card}
      data-border={withBorder}
      data-box-shadow={withBoxShadow}
    >
      <a className={styles.link} href={href} aria-label={ariaLabel}>
        <div className={styles.imageWrapper}>
          <Image
            src={imageURL}
            alt=""
            fill
            sizes="(min-width: 988px) 104px, 80px"
            className={styles.image}
            priority={priority}
            loading={priority ? undefined : 'eager'}
          />
        </div>
        <div className={styles.info}>
          {title && <p className={styles.title}>{title}</p>}
          <p className={styles.name}>{displayName}</p>
          <div className={styles.priceRow}>
            {displaySalePrice ? (
              <>
                <span className={styles.salePrice}>{displaySalePrice}</span>
                <s className={styles.originalPrice}>{displayPrice}</s>
                <span className={styles.badge}>
                  {t('productStrip.discountBadge', {
                    percent: discountPercent,
                  })}
                </span>
              </>
            ) : (
              <span className={styles.price}>{displayPrice}</span>
            )}
          </div>
        </div>
      </a>
    </article>
  );
};

export default MiniProductCard;
